import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FileText, TrendingUp, Building2, Euro, Calendar, Search, 
  ChevronRight, Zap, BarChart3, Filter, X, Clock, Trash2, Save, Shield,
  Lock, Unlock, Pencil, Plus, AlertTriangle, Share2, CheckCircle2, FileSpreadsheet
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ShareModal from '../components/ShareModal';
import EcheancesBanner from '../components/EcheancesBanner';
import { exportToExcel, exportMultiSheetExcel, fmtDateExcel } from '../utils/exportExcel';
import ShowMoreControls from '../components/ShowMoreControls';
import { listBrouillons, deleteBrouillon as apiDeleteBrouillon } from '../utils/brouillonsApi';
import { downloadPdf } from '../utils/downloadPdf';

// Détecte une incohérence entre heures de dépassement et CMDPS sur un rapport TURPE.
// Deux cas flagués :
//  1) Heures > 0 mais CMDPS = 0 (ou l'inverse) — incohérence stricte
//  2) Rapport possédant graphData mais sans bloc `calculs.depassements` — probablement
//     calculé avant le correctif de Fork 6 (donc potentiellement bug AGIBEL).
const detecterIncoherenceCMDPS = (rapport) => {
  const collect = (calc) => {
    if (!calc) return null;
    const dep = calc.depassements || {};
    const cmdps = (calc.composantes || {}).CMDPS || {};
    const hActuel = Number(dep.actuel?.heuresUniques || 0);
    const hPropose = Number(dep.propose?.heuresUniques || 0);
    const cActuel = Number(cmdps.actuel || 0);
    const cPropose = Number(cmdps.propose || 0);
    const actuelKO = (hActuel > 0 && cActuel <= 0.01) || (cActuel > 0.01 && hActuel === 0);
    const proposeKO = (hPropose > 0 && cPropose <= 0.01) || (cPropose > 0.01 && hPropose === 0);
    return actuelKO || proposeKO;
  };
  // Cas 1 : incohérence explicite
  if (rapport.calculs && collect(rapport.calculs)) return true;
  if (Array.isArray(rapport.pdls)) {
    for (const p of rapport.pdls) {
      if (collect(p.calculs)) return true;
    }
  }
  // Cas 2 : rapport suspect — PS hétérogènes par plage + graphData + dépassements "à zéro"
  // (soit depassements manquants, soit heures=0 ET CMDPS=0). Indique probablement un calcul
  // fait avec l'ancienne version (bug AGIBEL).
  const hasGraph = Array.isArray(rapport.graphData) && rapport.graphData.length > 0;
  if (hasGraph) {
    const c = rapport.calculs || {};
    const psByPlage = c.puissancesActuelles;
    if (psByPlage && typeof psByPlage === 'object') {
      const vals = Object.entries(psByPlage)
        .filter(([k]) => k !== 'max') // 'max' est un champ agrégé, pas une plage
        .map(([, v]) => Number(v) || 0)
        .filter(v => v > 0);
      const heterogene = new Set(vals).size > 1;
      if (heterogene) {
        const dep = c.depassements || {};
        const cmdps = (c.composantes || {}).CMDPS || {};
        const hA = Number(dep.actuel?.heuresUniques || 0);
        const cA = Number(cmdps.actuel || 0);
        const depMissing = !c.depassements;
        const allZero = hA === 0 && cA <= 0.01;
        if (depMissing || allZero) return true;
      }
    }
  }
  return false;
};

// ====== Définitions de colonnes Excel (réutilisées par export par type + export global) ======
const COLS_ENTREPRISES = [
  { key: 'nom', label: 'Entreprise' },
  { key: 'siren', label: 'SIREN' },
  { key: 'economies_ticfe', label: 'Économies TICFE (€)', format: (v) => Math.round(v || 0) },
  { key: 'economies_turpe', label: 'Économies TURPE (€)', format: (v) => Math.round(v || 0) },
  { key: 'economies_ticgn', label: 'Économies TICGN (€)', format: (v) => Math.round(v || 0) },
  { key: (r) => (r.economies_ticfe || 0) + (r.economies_turpe || 0) + (r.economies_ticgn || 0), label: 'Total économies (€)', format: (v) => Math.round(v || 0) },
  { key: 'nb_rapports_ticfe', label: 'Nb TICFE', format: (v) => Number(v || 0) },
  { key: 'nb_rapports_turpe', label: 'Nb TURPE', format: (v) => Number(v || 0) },
  { key: 'nb_rapports_ticgn', label: 'Nb TICGN', format: (v) => Number(v || 0) },
];
const COLS_TICFE = [
  { key: 'nom_entreprise', label: 'Client' },
  { key: 'siren', label: 'SIREN' },
  { key: 'eligibilite_globale', label: 'Éligibilité' },
  { key: 'economies_attestation_total', label: 'Éco. Attestation (€)', format: (v) => Math.round(v || 0) },
  { key: (r) => Object.values(r.economies_remboursement || {}).reduce((s, v) => s + v, 0), label: 'Éco. Remboursement (€)', format: (v) => Math.round(v || 0) },
  { key: (r) => (r.economies_attestation_total || 0) + Object.values(r.economies_remboursement || {}).reduce((s, v) => s + v, 0), label: 'Total économies (€)', format: (v) => Math.round(v || 0) },
  { key: 'created_at', label: 'Date création', format: fmtDateExcel },
  { key: 'locked', label: 'Figé', format: (v) => (v ? 'Oui' : 'Non') },
];
const COLS_TURPE = [
  { key: (r) => r.client?.nom || r.nom_entreprise || '-', label: 'Client' },
  { key: (r) => (r.pdls && r.pdls.length > 0) ? 'Multi-sites' : 'Mono-PDL', label: 'Type' },
  { key: (r) => (r.pdls && r.pdls.length > 0) ? `${r.pdls.length} sites` : (r.pdl?.numero || '-'), label: 'PDL / Sites' },
  { key: (r) => (r.pdls && r.pdls.length > 0) ? 'Multi-sites' : (r.formuleActuelle || '-'), label: 'Formule' },
  { key: (r) => r.economies_total ?? r.economies?.total ?? 0, label: 'Économies (€)', format: (v) => Math.round(v || 0) },
  { key: 'created_at', label: 'Date création', format: fmtDateExcel },
  { key: 'locked', label: 'Figé', format: (v) => (v ? 'Oui' : 'Non') },
];
const COLS_TICGN = [
  { key: 'nom_entreprise', label: 'Entreprise' },
  { key: 'siren', label: 'SIREN' },
  { key: 'total_conso_mwh', label: 'Consommation (MWh)', format: (v) => Number((v || 0).toFixed(2)) },
  { key: 'eligibilite_globale', label: 'Éligibilité' },
  { key: 'soumis_quotas_co2', label: 'Soumis CO2 (SIREN)', format: (v) => (v ? 'Oui' : 'Non') },
  { key: 'valeur_production_eligible', label: 'VP éligible (>3%)', format: (v) => (v ? 'Oui' : 'Non') },
  { key: 'economies_attestation_total', label: 'Éco. Attestation (€)', format: (v) => Math.round(v || 0) },
  { key: (r) => Object.values(r.economies_remboursement || {}).reduce((s, v) => s + v, 0), label: 'Éco. Remboursement (€)', format: (v) => Math.round(v || 0) },
  { key: (r) => (r.economies_attestation_total || 0) + Object.values(r.economies_remboursement || {}).reduce((s, v) => s + v, 0), label: 'Total économies (€)', format: (v) => Math.round(v || 0) },
  { key: 'created_at', label: 'Date création', format: fmtDateExcel },
  { key: 'locked', label: 'Figé', format: (v) => (v ? 'Oui' : 'Non') },
];


const DashboardUnified = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userPermissions, canCreateRapport, isAdmin, hasPermission } = useAuth();
  // Helpers permissions par taxe (couvre custom roles)
  const canCreateTicfe = () => hasPermission && hasPermission('ticfe_rapports', 'create');
  const canEditTicfe = () => hasPermission && hasPermission('ticfe_rapports', 'edit');
  const canDeleteTicfe = () => hasPermission && hasPermission('ticfe_rapports', 'delete');
  const canCreateTicgn = () => hasPermission && hasPermission('ticgn_rapports', 'create');
  const canEditTicgn = () => hasPermission && hasPermission('ticgn_rapports', 'edit');
  const canDeleteTicgn = () => hasPermission && hasPermission('ticgn_rapports', 'delete');
  const canCreateTurpe = () => hasPermission && hasPermission('turpe_rapports', 'create');
  const canEditTurpe = () => hasPermission && hasPermission('turpe_rapports', 'edit');
  const canDeleteTurpe = () => hasPermission && hasPermission('turpe_rapports', 'delete');
  
  // Onglet actif (entreprise, ticfe, turpe)
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'entreprise');
  
  // États
  const [stats, setStats] = useState(null);
  const [rapportsTICFE, setRapportsTICFE] = useState([]);
  const [rapportsTURPE, setRapportsTURPE] = useState([]);
  const [rapportsTICGN, setRapportsTICGN] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [agenceFilter, setAgenceFilter] = useState('');
  const [commercialFilter, setCommercialFilter] = useState('');
  const [agencesList, setAgencesList] = useState([]);
  const [commerciauxList, setCommerciauxList] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Pagination incrémentale — nb de lignes visibles par liste (palier de PAGE_SIZE)
  const PAGE_SIZE = 25;
  const [visibleTicfe, setVisibleTicfe] = useState(PAGE_SIZE);
  const [visibleTicgn, setVisibleTicgn] = useState(PAGE_SIZE);
  const [visibleTurpe, setVisibleTurpe] = useState(PAGE_SIZE);
  const [visibleEntreprises, setVisibleEntreprises] = useState(PAGE_SIZE);
  
  // Brouillons (chargés via API serveur — TICFE + TICGN + TURPE)
  const [brouillonsServer, setBrouillonsServer] = useState([]);
  const [loadingBrouillons, setLoadingBrouillons] = useState(false);

  // Partage en lecture seule
  const [shareTarget, setShareTarget] = useState(null); // {type, id, label} | null

  // IDs des rapports en alerte CERFA (pour afficher un badge sur les lignes)
  const [echeanceIds, setEcheanceIds] = useState({ ticfe: new Set(), ticgn: new Set() });
  const handleEcheancesChange = React.useCallback((items) => {
    const ticfe = new Set();
    const ticgn = new Set();
    (items || []).forEach((it) => {
      if (it.type === 'ticfe') ticfe.add(it.id);
      else if (it.type === 'ticgn') ticgn.add(it.id);
    });
    setEcheanceIds({ ticfe, ticgn });
  }, []);

  useEffect(() => {
    fetchData();
    loadBrouillons();
  }, []);

  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab, setSearchParams]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch stats
      const statsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/stats`, { headers });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      // Fetch TICFE rapports
      const ticfeRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rapports/list`, { headers });
      if (ticfeRes.ok) {
        setRapportsTICFE(await ticfeRes.json());
      }

      // Fetch TURPE rapports
      const turpeRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports/list`, { headers });
      if (turpeRes.ok) {
        setRapportsTURPE(await turpeRes.json());
      }

      // Fetch TICGN rapports
      const ticgnRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ticgn/rapports/list`, { headers });
      if (ticgnRes.ok) {
        setRapportsTICGN(await ticgnRes.json());
      }

      // Fetch Agences + Commerciaux (pour les filtres)
      const [agRes, comRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agences`, { headers }).catch(() => null),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/commerciaux`, { headers }).catch(() => null),
      ]);
      if (agRes && agRes.ok) {
        const ags = await agRes.json();
        setAgencesList(Array.isArray(ags) ? ags : []);
      }
      if (comRes && comRes.ok) {
        const coms = await comRes.json();
        setCommerciauxList(Array.isArray(coms) ? coms : []);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBrouillons = async () => {
    setLoadingBrouillons(true);
    try {
      const list = await listBrouillons();
      setBrouillonsServer(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Erreur chargement brouillons');
      setBrouillonsServer([]);
    } finally {
      setLoadingBrouillons(false);
    }
  };

  const supprimerBrouillon = async (id) => {
    if (!window.confirm('Supprimer ce brouillon ?')) return;
    try {
      await apiDeleteBrouillon(id);
      setBrouillonsServer(prev => prev.filter(b => b.id !== id));
    } catch (e) {
      console.error('Erreur suppression brouillon:', e);
      alert('Erreur lors de la suppression du brouillon.');
    }
  };

  // Suggestions pour autocomplétion
  const allClientNames = useMemo(() => {
    const names = new Set();
    rapportsTICFE.forEach(r => r.nom_entreprise && names.add(r.nom_entreprise));
    rapportsTURPE.forEach(r => {
      const nom = r.client?.nom || r.nom_entreprise;
      if (nom) names.add(nom);
    });
    return Array.from(names).sort();
  }, [rapportsTICFE, rapportsTURPE]);

  const suggestions = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];
    const query = searchQuery.toLowerCase();
    return allClientNames.filter(name => name.toLowerCase().includes(query)).slice(0, 5);
  }, [searchQuery, allClientNames]);

  // Filtrage des rapports
  const filterRapports = (rapports, type) => {
    return rapports.filter(r => {
      // Filtre recherche
      if (searchQuery) {
        const nom = type === 'ticfe' ? r.nom_entreprise : (r.client?.nom || r.nom_entreprise);
        if (!nom?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      }
      
      // Filtre dates
      const dateRapport = new Date(r.created_at || r.date_creation);
      if (dateDebut && dateRapport < new Date(dateDebut)) return false;
      if (dateFin && dateRapport > new Date(dateFin + 'T23:59:59')) return false;

      // Filtre agence (rapport.agence_id OU rapport.agence.id OU dérivé via commercial)
      if (agenceFilter) {
        const rAgenceId = String(
          r.agence_id ||
          r.agence?.id ||
          (commerciauxList.find(c => String(c.id) === String(r.commercial_id))?.agence_id || '')
        );
        if (rAgenceId !== String(agenceFilter)) return false;
      }
      // Filtre commercial
      if (commercialFilter) {
        if (String(r.commercial_id || '') !== String(commercialFilter)) return false;
      }
      return true;
    });
  };

  const rapportsTICFEAll = useMemo(() => filterRapports(rapportsTICFE, 'ticfe'), [rapportsTICFE, searchQuery, dateDebut, dateFin, agenceFilter, commercialFilter, commerciauxList]);
  const rapportsTURPEAll = useMemo(() => filterRapports(rapportsTURPE, 'turpe'), [rapportsTURPE, searchQuery, dateDebut, dateFin, agenceFilter, commercialFilter, commerciauxList]);
  const rapportsTICFEFiltres = useMemo(() => rapportsTICFEAll.slice(0, visibleTicfe), [rapportsTICFEAll, visibleTicfe]);
  const rapportsTURPEFiltres = useMemo(() => rapportsTURPEAll.slice(0, visibleTurpe), [rapportsTURPEAll, visibleTurpe]);

  // Entreprises filtrées
  const entreprisesAll = useMemo(() => {
    if (!stats?.entreprises) return [];
    return stats.entreprises
      .filter(e => !searchQuery || e.nom?.toLowerCase().includes(searchQuery.toLowerCase()) || e.siren?.includes(searchQuery))
      .sort((a, b) => ((b.economies_ticfe || 0) + (b.economies_turpe || 0) + (b.economies_ticgn || 0)) - ((a.economies_ticfe || 0) + (a.economies_turpe || 0) + (a.economies_ticgn || 0)));
  }, [stats, searchQuery]);
  const entreprisesFiltrees = useMemo(() => entreprisesAll.slice(0, visibleEntreprises), [entreprisesAll, visibleEntreprises]);

  // TICGN : liste filtrée (recherche + agence + commercial) puis paginée
  const rapportsTICGNAll = useMemo(() => rapportsTICGN.filter(r => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!(r.nom_entreprise || '').toLowerCase().includes(q) && !(r.siren || '').includes(q)) return false;
    }
    if (agenceFilter) {
      const rAgenceId = String(
        r.agence_id || r.agence?.id ||
        (commerciauxList.find(c => String(c.id) === String(r.commercial_id))?.agence_id || '')
      );
      if (rAgenceId !== String(agenceFilter)) return false;
    }
    if (commercialFilter && String(r.commercial_id || '') !== String(commercialFilter)) return false;
    return true;
  }), [rapportsTICGN, searchQuery, agenceFilter, commercialFilter, commerciauxList]);
  const rapportsTICGNFiltres = useMemo(() => rapportsTICGNAll.slice(0, visibleTicgn), [rapportsTICGNAll, visibleTicgn]);

  const formatCurrency = (value) => {
    if (!value) return '0 €';
    return value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setDateDebut('');
    setDateFin('');
    setAgenceFilter('');
    setCommercialFilter('');
  };

  // ====== EXPORTS EXCEL ======
  const exportEntreprisesExcel = () => {
    exportToExcel(entreprisesAll, COLS_ENTREPRISES, "Synthèse d'Optimisation", 'synthese_optimisation');
  };

  const exportTicfeExcel = () => {
    exportToExcel(rapportsTICFEAll, COLS_TICFE, 'TICFE', 'rapports_ticfe');
  };

  const exportTurpeExcel = () => {
    exportToExcel(rapportsTURPEAll, COLS_TURPE, 'TURPE', 'rapports_turpe');
  };

  const exportTicgnExcel = () => {
    exportToExcel(rapportsTICGNAll, COLS_TICGN, 'TICGN', 'rapports_ticgn');
  };

  // Export GLOBAL : un seul fichier Excel multi-onglets (Synthèse + TICFE + TURPE + TICGN)
  const exportGlobalExcel = () => {
    exportMultiSheetExcel(
      [
        { rows: entreprisesAll, columns: COLS_ENTREPRISES, sheetName: "Synthèse" },
        { rows: rapportsTICFEAll, columns: COLS_TICFE, sheetName: 'TICFE' },
        { rows: rapportsTURPEAll, columns: COLS_TURPE, sheetName: 'TURPE' },
        { rows: rapportsTICGNAll, columns: COLS_TICGN, sheetName: 'TICGN' },
      ],
      'export_global_optimisations'
    );
  };

  // Suppression des rapports (admin uniquement)
  const deleteRapportTICFE = async (rapportId, nomEntreprise) => {
    if (!window.confirm(`Supprimer le rapport TICFE de "${nomEntreprise}" ?\n\nCette action est irréversible.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/rapports/${rapportId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        // Retirer le rapport de la liste locale
        setRapportsTICFE(prev => prev.filter(r => r.id !== rapportId));
        // Rafraîchir les données
        fetchData();
      } else {
        const data = await response.json();
        alert(data.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression du rapport');
    }
  };

  const deleteRapportTURPE = async (rapportId, clientNom) => {
    if (!window.confirm(`Supprimer le rapport TURPE de "${clientNom}" ?\n\nCette action est irréversible.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports/${rapportId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        // Retirer le rapport de la liste locale
        setRapportsTURPE(prev => prev.filter(r => r.id !== rapportId));
        // Rafraîchir les données
        fetchData();
      } else {
        const data = await response.json();
        alert(data.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression du rapport');
    }
  };

  const lockRapport = async (type, rapportId, nom) => {
    if (!window.confirm(`Figer le rapport de "${nom}" ?\n\nUne fois figé, il ne pourra plus être modifié.`)) return;
    try {
      const token = localStorage.getItem('token');
      const endpoint = type === 'ticfe' ? `/api/rapports/${rapportId}/lock`
        : type === 'ticgn' ? `/api/ticgn/rapports/${rapportId}/lock`
        : `/api/turpe/rapports/${rapportId}/lock`;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchData();
      } else {
        const data = await response.json();
        alert(data.detail || 'Erreur lors du verrouillage');
      }
    } catch (error) {
      alert('Erreur lors du verrouillage du rapport');
    }
  };

  const unlockRapport = async (type, rapportId) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = type === 'ticfe' ? `/api/rapports/${rapportId}/unlock`
        : type === 'ticgn' ? `/api/ticgn/rapports/${rapportId}/unlock`
        : `/api/turpe/rapports/${rapportId}/unlock`;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchData();
      } else {
        const data = await response.json();
        alert(data.detail || 'Erreur lors du déverrouillage');
      }
    } catch (error) {
      alert('Erreur lors du déverrouillage du rapport');
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#397DFF]"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'entreprise', label: "Synthèse d'Optimisation", icon: Building2 },
    { id: 'ticfe', label: 'TICFE', icon: Zap },
    { id: 'ticgn', label: 'TICGN', icon: () => <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 12c-2-2.67-4-4-4-5.5A4 4 0 0 1 12 3a4 4 0 0 1 4 3.5c0 1.5-2 2.83-4 5.5Z"/><path d="M12 12c2 2.67 4 4 4 5.5A4 4 0 0 1 12 21a4 4 0 0 1-4-3.5c0-1.5 2-2.83 4-5.5Z"/></svg> },
    { id: 'turpe', label: 'TURPE', icon: BarChart3 },
    { id: 'brouillons', label: 'Brouillons', icon: Save }
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-[#191D1E] tracking-tight">Tableau de bord</h1>
          <p className="text-[#687175] mt-1">
            Vue d'ensemble de vos analyses énergétiques
            {userPermissions.role !== 'admin' && userPermissions.role !== 'energy_manager' && userPermissions.agence && (
              <span className="ml-2 px-2 py-0.5 bg-[#E8F4FD] text-[#397DFF] rounded text-xs">
                <Shield className="w-3 h-3 inline mr-1" />
                {userPermissions.agence}
              </span>
            )}
          </p>
        </div>
        <button
          data-testid="export-global-excel"
          onClick={exportGlobalExcel}
          className="shrink-0 text-sm px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition inline-flex items-center gap-2"
          title="Exporter TOUS les rapports (Synthèse + TICFE + TURPE + TICGN) dans un seul fichier Excel multi-onglets"
        >
          <FileSpreadsheet className="w-4 h-4" /> Export global Excel
        </button>
      </div>

      {/* Bandeau échéances CERFA */}
      <EcheancesBanner onEcheancesChange={handleEcheancesChange} />

      {/* Onglets */}
      <div className="flex gap-1 bg-[#F5F6F9] p-1 rounded-lg mb-6 w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-[#191D1E] shadow-sm'
                : 'text-[#687175] hover:text-[#191D1E]'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-white border border-[#BFC6CC] rounded-lg p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Recherche avec autocomplétion */}
          <div className="flex-1 min-w-[250px] relative">
            <label className="block text-xs font-medium text-[#687175] mb-1">Rechercher un client</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#687175]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="Nom de l'entreprise..."
                className="w-full pl-10 pr-4 py-2 border border-[#BFC6CC] rounded-lg text-sm focus:ring-2 focus:ring-[#397DFF] focus:border-transparent"
              />
              {/* Suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-[#BFC6CC] rounded-lg shadow-lg">
                  {suggestions.map((name, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setSearchQuery(name);
                        setShowSuggestions(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-[#F5F6F9] first:rounded-t-lg last:rounded-b-lg"
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Filtre date début */}
          <div className="w-40">
            <label className="block text-xs font-medium text-[#687175] mb-1">Date début</label>
            <input
              type="date"
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
              className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm focus:ring-2 focus:ring-[#397DFF]"
            />
          </div>

          {/* Filtre date fin */}
          <div className="w-40">
            <label className="block text-xs font-medium text-[#687175] mb-1">Date fin</label>
            <input
              type="date"
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
              className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm focus:ring-2 focus:ring-[#397DFF]"
            />
          </div>

          {/* Filtre Agence */}
          <div className="w-48">
            <label className="block text-xs font-medium text-[#687175] mb-1">Agence</label>
            <select
              value={agenceFilter}
              onChange={(e) => {
                setAgenceFilter(e.target.value);
                // Si le commercial sélectionné n'appartient pas à la nouvelle agence, on le réinitialise
                if (commercialFilter) {
                  const com = commerciauxList.find(c => String(c.id) === String(commercialFilter));
                  if (e.target.value && com && String(com.agence_id) !== String(e.target.value)) {
                    setCommercialFilter('');
                  }
                }
              }}
              className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm focus:ring-2 focus:ring-[#397DFF] bg-white"
              data-testid="filter-agence"
            >
              <option value="">Toutes les agences</option>
              {agencesList.map(a => (
                <option key={a.id} value={a.id}>{a.nom}</option>
              ))}
            </select>
          </div>

          {/* Filtre Commercial */}
          <div className="w-56">
            <label className="block text-xs font-medium text-[#687175] mb-1">Commercial</label>
            <select
              value={commercialFilter}
              onChange={(e) => setCommercialFilter(e.target.value)}
              className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm focus:ring-2 focus:ring-[#397DFF] bg-white"
              data-testid="filter-commercial"
            >
              <option value="">Tous les commerciaux</option>
              {commerciauxList
                .filter(c => !agenceFilter || String(c.agence_id) === String(agenceFilter))
                .map(c => (
                  <option key={c.id} value={c.id}>
                    {(c.prenom ? c.prenom + ' ' : '') + (c.nom || '')}{c.agence ? ` — ${c.agence}` : ''}
                  </option>
                ))}
            </select>
          </div>

          {/* Bouton reset */}
          {(searchQuery || dateDebut || dateFin || agenceFilter || commercialFilter) && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-3 py-2 text-sm text-[#687175] hover:text-[#191D1E] border border-[#BFC6CC] rounded-lg"
              data-testid="filter-clear"
            >
              <X className="w-4 h-4" /> Effacer
            </button>
          )}
        </div>
      </div>

      {/* Contenu selon l'onglet actif */}
      
      {/* === ONGLET ENTREPRISE === */}
      {activeTab === 'entreprise' && (
        <div>
          {/* KPIs */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
              <div className="text-sm text-[#687175] mb-1">Entreprises</div>
              <div className="text-2xl font-bold text-[#191D1E]">{stats?.entreprises?.length || 0}</div>
            </div>
            <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
              <div className="text-sm text-[#687175] mb-1">Économies TICFE</div>
              <div className="text-2xl font-bold text-[#F9C80E]">{formatCurrency(stats?.ticfe?.economies_total)}</div>
            </div>
            <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
              <div className="text-sm text-[#687175] mb-1">Économies TURPE</div>
              <div className="text-2xl font-bold text-[#397DFF]">{formatCurrency(stats?.turpe?.economies_total)}</div>
            </div>
            <div className="bg-gradient-to-r from-[#10B981] to-[#059669] rounded-lg p-4 text-white">
              <div className="text-sm text-emerald-100 mb-1">Total Économies</div>
              <div className="text-2xl font-bold">
                {formatCurrency((stats?.ticfe?.economies_total || 0) + (stats?.turpe?.economies_total || 0))}
              </div>
            </div>
          </div>

          {/* Liste des entreprises */}
          <div className="bg-white border border-[#BFC6CC] rounded-lg">
            <div className="px-6 py-4 border-b border-[#BFC6CC] flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-[#191D1E]">Entreprises</h2>
                <p className="text-sm text-[#687175]">
                  {`${Math.min(visibleEntreprises, entreprisesAll.length)} sur ${entreprisesAll.length} · triées par économies totales`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <ShowMoreControls
                  visible={visibleEntreprises}
                  total={entreprisesAll.length}
                  pageSize={PAGE_SIZE}
                  onMore={() => setVisibleEntreprises(v => v + PAGE_SIZE)}
                  onReset={() => setVisibleEntreprises(PAGE_SIZE)}
                  testid="paginate-entreprises"
                />
                {entreprisesAll.length > 0 && (
                  <button
                    data-testid="export-entreprises-excel"
                    onClick={exportEntreprisesExcel}
                    className="text-sm px-3 py-1.5 rounded-lg border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition inline-flex items-center gap-1.5"
                    title="Télécharger la liste filtrée au format Excel"
                  >
                    <FileSpreadsheet className="w-4 h-4" /> Excel
                  </button>
                )}
              </div>
            </div>
            
            {entreprisesFiltrees.length === 0 ? (
              <div className="p-8 text-center text-[#687175]">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-[#BFC6CC]" />
                <p>Aucune entreprise trouvée</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-[#F5F6F9] text-xs text-[#687175] uppercase">
                  <tr>
                    <th className="text-left px-6 py-3">Entreprise</th>
                    <th className="text-left px-6 py-3">SIREN</th>
                    <th className="text-right px-6 py-3">Éco. TICFE</th>
                    <th className="text-right px-6 py-3">Éco. TURPE</th>
                    <th className="text-right px-6 py-3">Éco. TICGN</th>
                    <th className="text-right px-6 py-3">Total</th>
                    <th className="text-center px-6 py-3">Rapports</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {entreprisesFiltrees.map((e, idx) => (
                    <tr key={idx} className="border-t border-[#BFC6CC] hover:bg-[#F5F6F9]">
                      <td className="px-6 py-4 font-medium text-[#191D1E]">{e.nom || '-'}</td>
                      <td className="px-6 py-4 text-[#687175] font-mono text-sm">{e.siren}</td>
                      <td className="px-6 py-4 text-right text-[#F9C80E] font-medium">{formatCurrency(e.economies_ticfe)}</td>
                      <td className="px-6 py-4 text-right text-[#397DFF] font-medium">{formatCurrency(e.economies_turpe)}</td>
                      <td className="px-6 py-4 text-right text-[#FF6B35] font-medium">{formatCurrency(e.economies_ticgn || 0)}</td>
                      <td className="px-6 py-4 text-right text-[#10B981] font-bold">{formatCurrency((e.economies_ticfe || 0) + (e.economies_turpe || 0) + (e.economies_ticgn || 0))}</td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center gap-1 text-sm">
                          <span className="px-2 py-0.5 bg-[#FFF9E6] text-[#F9C80E] rounded text-xs">{e.nb_rapports_ticfe} TICFE</span>
                          <span className="px-2 py-0.5 bg-[#E8F4FD] text-[#397DFF] rounded text-xs">{e.nb_rapports_turpe} TURPE</span>
                          {(e.nb_rapports_ticgn || 0) > 0 && (
                            <span className="px-2 py-0.5 bg-orange-50 text-[#FF6B35] rounded text-xs">{e.nb_rapports_ticgn} TICGN</span>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <Link
                          to={`/entreprises/${e.siren}`}
                          className="text-[#397DFF] hover:text-[#4672B6] text-sm flex items-center gap-1"
                        >
                          Fiche <ChevronRight className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* === ONGLET TICFE === */}
      {activeTab === 'ticfe' && (
        <div>
          {/* KPIs TICFE */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
              <div className="text-sm text-[#687175] mb-1">Rapports TICFE</div>
              <div className="text-2xl font-bold text-[#191D1E]">{stats?.ticfe?.nb_rapports || 0}</div>
            </div>
            <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
              <div className="text-sm text-[#687175] mb-1">Éligibles</div>
              <div className="text-2xl font-bold text-[#10B981]">{stats?.ticfe?.nb_eligible || 0}</div>
            </div>
            <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
              <div className="text-sm text-[#687175] mb-1">Éco. Attestation</div>
              <div className="text-2xl font-bold text-[#397DFF]">{formatCurrency(stats?.ticfe?.economies_attestation)}</div>
            </div>
            <div className="bg-gradient-to-r from-[#F9C80E] to-[#F59E0B] rounded-lg p-4 text-white">
              <div className="text-sm text-yellow-100 mb-1">Total Économies</div>
              <div className="text-2xl font-bold">{formatCurrency(stats?.ticfe?.economies_total)}</div>
            </div>
          </div>

          {/* Brouillons TICFE — résumé rapide (vue complète : onglet Brouillons) */}
          {(() => {
            const brouillonsTICFEServer = brouillonsServer.filter(b => b.type === 'ticfe');
            if (brouillonsTICFEServer.length === 0) return null;
            return (
              <div className="mb-6 bg-[#FFF9E6] border border-[#F9C80E] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Save className="w-4 h-4 text-[#F9C80E]" />
                  <span className="font-medium text-[#191D1E]">Brouillons TICFE ({brouillonsTICFEServer.length})</span>
                  <button onClick={() => setActiveTab('brouillons')} className="ml-auto text-xs text-[#397DFF] hover:underline">Voir tous</button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {brouillonsTICFEServer.slice(0, 3).map(b => (
                    <div key={b.id} className="bg-white rounded-lg px-3 py-2 border border-[#BFC6CC] flex items-center gap-2 text-sm">
                      <span>{b.nom_entreprise || b.payload?.formData?.nom_entreprise || 'Sans nom'}</span>
                      <button onClick={() => navigate(`/rapports/create?brouillon=${b.id}`)} className="text-[#397DFF]">Reprendre</button>
                      <button onClick={() => supprimerBrouillon(b.id)} className="text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Liste rapports TICFE */}
          <div className="bg-white border border-[#BFC6CC] rounded-lg">
            <div className="px-6 py-4 border-b border-[#BFC6CC] flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-[#191D1E]">Rapports TICFE</h2>
                <p className="text-sm text-[#687175]">
                  {`${Math.min(visibleTicfe, rapportsTICFEAll.length)} sur ${rapportsTICFEAll.length} (plus récents en premier)`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <ShowMoreControls
                  visible={visibleTicfe}
                  total={rapportsTICFEAll.length}
                  pageSize={PAGE_SIZE}
                  onMore={() => setVisibleTicfe(v => v + PAGE_SIZE)}
                  onReset={() => setVisibleTicfe(PAGE_SIZE)}
                  testid="paginate-ticfe"
                  colorClass="border-[#F9C80E] text-[#B45309] hover:bg-[#F9C80E]"
                />
                {rapportsTICFEAll.length > 0 && (
                  <button
                    data-testid="export-ticfe-excel"
                    onClick={exportTicfeExcel}
                    className="text-sm px-3 py-1.5 rounded-lg border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition inline-flex items-center gap-1.5"
                    title="Télécharger la liste filtrée au format Excel"
                  >
                    <FileSpreadsheet className="w-4 h-4" /> Excel
                  </button>
                )}
              </div>
            </div>
            
            {rapportsTICFEFiltres.length === 0 ? (
              <div className="p-8 text-center text-[#687175]">
                <FileText className="w-12 h-12 mx-auto mb-3 text-[#BFC6CC]" />
                <p>Aucun rapport TICFE trouvé</p>
                {canCreateTicfe() && (
                  <Link to="/rapports/create" className="text-[#397DFF] hover:underline mt-2 inline-block">Créer un rapport</Link>
                )}
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-[#F5F6F9] text-xs text-[#687175] uppercase">
                  <tr>
                    <th className="text-left px-6 py-3">Client</th>
                    <th className="text-left px-6 py-3">SIREN</th>
                    <th className="text-center px-6 py-3">Éligibilité</th>
                    <th className="text-right px-6 py-3">Économies</th>
                    <th className="text-left px-6 py-3">Date</th>
                    <th className="px-6 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rapportsTICFEFiltres.map((r, idx) => (
                    <tr key={idx} className="border-t border-[#BFC6CC] hover:bg-[#F5F6F9]">
                      <td className="px-6 py-4 font-medium text-[#191D1E]">
                        {r.nom_entreprise}
                        {echeanceIds.ticfe.has(r.id) && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300 align-middle"
                            title="Échéance CERFA proche — voir bandeau"
                            data-testid={`echeance-badge-ticfe-${r.id}`}
                          >
                            <AlertTriangle className="w-3 h-3" /> Échéance proche
                          </span>
                        )}
                        {r.client_confirmed && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300 align-middle"
                            title={`Client confirmé le ${new Date(r.client_confirmed.at).toLocaleDateString('fr-FR')} par ${r.client_confirmed.by?.email || '—'}`}
                            data-testid={`client-confirmed-badge-ticfe-${r.id}`}
                          >
                            <CheckCircle2 className="w-3 h-3" /> Client OK
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-[#687175] font-mono text-sm">{r.siren}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          r.eligibilite_globale === 'ELIGIBLE' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {r.eligibilite_globale}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-[#10B981]">
                        {formatCurrency((r.economies_attestation_total || 0) + Object.values(r.economies_remboursement || {}).reduce((s,v) => s+v, 0))}
                      </td>
                      <td className="px-6 py-4 text-[#687175] text-sm">{formatDate(r.created_at)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          {r.locked && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs mr-1 flex items-center gap-1">
                              <Lock className="w-3 h-3" /> Figé
                            </span>
                          )}
                          <Link to={`/rapports/ticfe/${r.id}`} target="_blank" rel="noopener noreferrer" className="text-[#397DFF] hover:text-[#4672B6] text-sm">
                            Consulter
                          </Link>
                          <button
                            onClick={() => setShareTarget({ type: 'ticfe', id: r.id, label: r.nom_entreprise })}
                            className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                            title="Partager en lecture seule"
                            data-testid={`share-ticfe-${r.id}`}
                          >
                            <Share2 className="w-4 h-4" />
                          </button>
                          {canEditTicfe() && !r.locked && (
                            <Link
                              to={`/rapports/create?edit=${r.id}`}
                              className="p-1.5 text-[#F9C80E] hover:bg-yellow-50 rounded transition-colors"
                              title="Modifier ce rapport"
                              data-testid={`edit-ticfe-${r.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Link>
                          )}
                          {canEditTicfe() && !r.locked && (
                            <button
                              onClick={() => lockRapport('ticfe', r.id, r.nom_entreprise)}
                              className="p-1.5 text-gray-400 hover:bg-gray-50 rounded transition-colors"
                              title="Figer ce rapport"
                              data-testid={`lock-ticfe-${r.id}`}
                            >
                              <Lock className="w-4 h-4" />
                            </button>
                          )}
                          {canEditTicfe() && r.locked && (
                            <button
                              onClick={() => unlockRapport('ticfe', r.id)}
                              className="p-1.5 text-orange-400 hover:bg-orange-50 rounded transition-colors"
                              title="Défiger ce rapport"
                              data-testid={`unlock-ticfe-${r.id}`}
                            >
                              <Unlock className="w-4 h-4" />
                            </button>
                          )}
                          {canDeleteTicfe() && !r.locked && (
                            <button
                              onClick={() => deleteRapportTICFE(r.id, r.nom_entreprise)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                              title="Supprimer ce rapport"
                              data-testid={`delete-ticfe-${r.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* === ONGLET TURPE === */}
      {activeTab === 'turpe' && (
        <div>
          {/* KPIs TURPE */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
              <div className="text-sm text-[#687175] mb-1">Rapports TURPE</div>
              <div className="text-2xl font-bold text-[#191D1E]">{stats?.turpe?.nb_rapports || 0}</div>
            </div>
            <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
              <div className="text-sm text-[#687175] mb-1">Économies identifiées</div>
              <div className="text-2xl font-bold text-[#397DFF]">{formatCurrency(stats?.turpe?.economies_total)}</div>
            </div>
            <div className="bg-gradient-to-r from-[#397DFF] to-[#4672B6] rounded-lg p-4 text-white">
              <div className="text-sm text-blue-100 mb-1">Économie moyenne</div>
              <div className="text-2xl font-bold">
                {stats?.turpe?.nb_rapports > 0 
                  ? formatCurrency((stats?.turpe?.economies_total || 0) / stats.turpe.nb_rapports)
                  : '0 €'}
              </div>
            </div>
          </div>

          {/* Alerte globale : rapports avec CMDPS incohérent */}
          {(() => {
            const incohCount = rapportsTURPE.filter(detecterIncoherenceCMDPS).length;
            if (incohCount === 0 || !hasPermission?.('admin_turpe', 'edit')) return null;
            return (
              <div
                className="mb-6 bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-center gap-3"
                data-testid="alerte-cmdps-globale"
              >
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div className="flex-1 text-sm">
                  <span className="font-medium text-amber-900">
                    {incohCount} rapport{incohCount > 1 ? 's' : ''} TURPE avec un calcul de dépassements incohérent
                  </span>
                  <span className="text-amber-700 ml-2">
                    — CMDPS = 0 € alors que des heures de dépassement existent (ou inversement).
                  </span>
                </div>
                <Link
                  to="/admin/recompute-depassements"
                  className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 transition-colors"
                  data-testid="btn-goto-recompute"
                >
                  Corriger
                </Link>
              </div>
            );
          })()}

          {/* Brouillons TURPE — résumé rapide (vue complète : onglet Brouillons) */}
          {(() => {
            const brouillonsTURPEServer = brouillonsServer.filter(b => b.type === 'turpe');
            if (brouillonsTURPEServer.length === 0) return null;
            return (
              <div className="mb-6 bg-[#E8F4FD] border border-[#397DFF] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Save className="w-4 h-4 text-[#397DFF]" />
                  <span className="font-medium text-[#191D1E]">Brouillons TURPE ({brouillonsTURPEServer.length})</span>
                  <button onClick={() => setActiveTab('brouillons')} className="ml-auto text-xs text-[#397DFF] hover:underline">Voir tous</button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {brouillonsTURPEServer.slice(0, 3).map(b => (
                    <div key={b.id} className="bg-white rounded-lg px-3 py-2 border border-[#BFC6CC] flex items-center gap-2 text-sm">
                      <span>{b.nom_entreprise || b.payload?.formData?.nom_client || 'Sans nom'}</span>
                      <button onClick={() => navigate(`/analyses/turpe/create?brouillon=${b.id}`)} className="text-[#397DFF]">Reprendre</button>
                      <button onClick={() => supprimerBrouillon(b.id)} className="text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Liste rapports TURPE */}
          <div className="bg-white border border-[#BFC6CC] rounded-lg">
            <div className="px-6 py-4 border-b border-[#BFC6CC] flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-[#191D1E]">Rapports TURPE</h2>
                <p className="text-sm text-[#687175]">
                  {`${Math.min(visibleTurpe, rapportsTURPEAll.length)} sur ${rapportsTURPEAll.length} (plus récents en premier)`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <ShowMoreControls
                  visible={visibleTurpe}
                  total={rapportsTURPEAll.length}
                  pageSize={PAGE_SIZE}
                  onMore={() => setVisibleTurpe(v => v + PAGE_SIZE)}
                  onReset={() => setVisibleTurpe(PAGE_SIZE)}
                  testid="paginate-turpe"
                />
                {rapportsTURPEAll.length > 0 && (
                  <button
                    data-testid="export-turpe-excel"
                    onClick={exportTurpeExcel}
                    className="text-sm px-3 py-1.5 rounded-lg border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition inline-flex items-center gap-1.5"
                    title="Télécharger la liste filtrée au format Excel"
                  >
                    <FileSpreadsheet className="w-4 h-4" /> Excel
                  </button>
                )}
              </div>
            </div>
            
            {rapportsTURPEFiltres.length === 0 ? (
              <div className="p-8 text-center text-[#687175]">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 text-[#BFC6CC]" />
                <p>Aucun rapport TURPE trouvé</p>
                {canCreateTurpe() && (
                  <Link to="/analyses/turpe/create" className="text-[#397DFF] hover:underline mt-2 inline-block">Créer une analyse</Link>
                )}
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-[#F5F6F9] text-xs text-[#687175] uppercase">
                  <tr>
                    <th className="text-left px-6 py-3">Client</th>
                    <th className="text-left px-6 py-3">PDL / Sites</th>
                    <th className="text-left px-6 py-3">Formule</th>
                    <th className="text-right px-6 py-3">Économies</th>
                    <th className="text-left px-6 py-3">Date</th>
                    <th className="px-6 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rapportsTURPEFiltres.map((r, idx) => {
                    const isMulti = r.pdls && r.pdls.length > 0;
                    const clientNom = r.client?.nom || r.nom_entreprise || '-';
                    const formule = isMulti ? 'Multi-sites' : (r.formuleActuelle || '-');
                    const pdlInfo = isMulti ? `${r.pdls.length} sites` : (r.pdl?.numero?.slice(-6) || '-');
                    const alerteCMDPS = detecterIncoherenceCMDPS(r);
                    
                    return (
                      <tr key={idx} className="border-t border-[#BFC6CC] hover:bg-[#F5F6F9]">
                        <td className="px-6 py-4 font-medium text-[#191D1E]">
                          <div className="flex items-center gap-2">
                            <span>{clientNom}</span>
                            {alerteCMDPS && (
                              <Link
                                to="/admin/recompute-depassements"
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-amber-100 text-amber-800 border border-amber-300 rounded hover:bg-amber-200 transition-colors"
                                title="CMDPS incohérent avec les heures de dépassement — cliquez pour recalculer depuis l'admin"
                                data-testid={`badge-cmdps-alert-${r.id}`}
                              >
                                <AlertTriangle className="w-3 h-3" />
                                Calcul à vérifier
                              </Link>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-[#687175] font-mono text-sm">{pdlInfo}</td>
                        <td className="px-6 py-4 text-[#687175]">{formule}</td>
                        <td className="px-6 py-4 text-right font-medium text-[#10B981]">
                          {formatCurrency(r.economies)}
                        </td>
                        <td className="px-6 py-4 text-[#687175] text-sm">{formatDate(r.created_at)}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-1">
                            {r.locked && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs mr-1 flex items-center gap-1">
                                <Lock className="w-3 h-3" /> Figé
                              </span>
                            )}
                            {r.validity_response && (
                              <span
                                className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs mr-1 flex items-center gap-1"
                                title={`Réponse client : ${r.validity_response.client?.prenom || ''} ${r.validity_response.client?.nom || ''} (${r.validity_response.client?.email || ''})`}
                                data-testid={`validity-badge-${r.id}`}
                              >
                                <CheckCircle2 className="w-3 h-3" /> Validé client
                              </span>
                            )}
                            <Link 
                              to={isMulti ? `/rapports/turpe/multi/${r.id}` : `/rapports/turpe/${r.id}`} 
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#397DFF] hover:text-[#4672B6] text-sm"
                            >
                              Consulter
                            </Link>
                            <button
                              onClick={() => setShareTarget({ type: isMulti ? 'turpe_multi' : 'turpe', id: r.id, label: r.client?.nom })}
                              className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                              title="Partager en lecture seule"
                              data-testid={`share-turpe-${r.id}`}
                            >
                              <Share2 className="w-4 h-4" />
                            </button>
                            {canEditTurpe() && !r.locked && (
                              <Link
                                to={`/turpe/create?edit=${r.id}`}
                                className="p-1.5 text-[#F9C80E] hover:bg-yellow-50 rounded transition-colors"
                                title="Modifier ce rapport"
                                data-testid={`edit-turpe-${r.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Link>
                            )}
                            {canEditTurpe() && !r.locked && (
                              <button
                                onClick={() => lockRapport('turpe', r.id, clientNom)}
                                className="p-1.5 text-gray-400 hover:bg-gray-50 rounded transition-colors"
                                title="Figer ce rapport"
                                data-testid={`lock-turpe-${r.id}`}
                              >
                                <Lock className="w-4 h-4" />
                              </button>
                            )}
                            {canEditTurpe() && r.locked && (
                              <button
                                onClick={() => unlockRapport('turpe', r.id)}
                                className="p-1.5 text-orange-400 hover:bg-orange-50 rounded transition-colors"
                                title="Défiger ce rapport"
                                data-testid={`unlock-turpe-${r.id}`}
                              >
                                <Unlock className="w-4 h-4" />
                              </button>
                            )}
                            {canDeleteTurpe() && !r.locked && (
                              <button
                                onClick={() => deleteRapportTURPE(r.id, clientNom)}
                                className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                                title="Supprimer ce rapport"
                                data-testid={`delete-turpe-${r.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* === ONGLET TICGN === */}
      {activeTab === 'ticgn' && (
        <div>
          {/* KPIs TICGN */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
              <div className="text-sm text-[#687175] mb-1">Rapports TICGN</div>
              <div className="text-2xl font-bold text-[#191D1E]">{stats?.ticgn?.nb_rapports || rapportsTICGN.length}</div>
            </div>
            <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
              <div className="text-sm text-[#687175] mb-1">Clients éligibles</div>
              <div className="text-2xl font-bold text-emerald-600">{stats?.ticgn?.nb_eligible || rapportsTICGN.filter(r => r.eligibilite_globale === 'ELIGIBLE').length}</div>
            </div>
            <div className="bg-white border border-[#BFC6CC] rounded-lg p-4">
              <div className="text-sm text-[#687175] mb-1">Éco. Attestation</div>
              <div className="text-2xl font-bold text-emerald-600">{formatCurrency(stats?.ticgn?.economies_attestation || rapportsTICGN.reduce((s, r) => s + (r.economies_attestation_total || 0), 0))}</div>
            </div>
            <div className="bg-gradient-to-r from-[#F97316] to-[#EA580C] rounded-lg p-4 text-white">
              <div className="text-sm text-orange-100 mb-1">Total Économies TICGN</div>
              <div className="text-2xl font-bold">{formatCurrency(stats?.ticgn?.economies_total || 0)}</div>
            </div>
          </div>

          {/* Bouton création */}
          {canCreateTicgn() && (
            <div className="mb-6">
              <button onClick={() => navigate('/rapports/ticgn/create')}
                className="flex items-center gap-2 bg-[#F97316] text-white px-5 py-2.5 rounded-lg font-medium hover:bg-[#EA580C] transition-colors"
                data-testid="create-ticgn-btn">
                <Plus className="w-5 h-5" /> Nouveau rapport TICGN
              </button>
            </div>
          )}

          {/* Liste rapports TICGN */}
          <div className="bg-white border border-[#BFC6CC] rounded-lg">
            {rapportsTICGN.length > 0 && (
              <div className="px-4 py-3 border-b border-[#BFC6CC] flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-[#191D1E]">Rapports TICGN</h2>
                  <p className="text-sm text-[#687175]">
                    {`${Math.min(visibleTicgn, rapportsTICGNAll.length)} sur ${rapportsTICGNAll.length} rapport(s)`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <ShowMoreControls
                    visible={visibleTicgn}
                    total={rapportsTICGNAll.length}
                    pageSize={PAGE_SIZE}
                    onMore={() => setVisibleTicgn(v => v + PAGE_SIZE)}
                    onReset={() => setVisibleTicgn(PAGE_SIZE)}
                    testid="paginate-ticgn"
                    colorClass="border-orange-600 text-orange-700 hover:bg-orange-600"
                  />
                  <button
                    data-testid="export-ticgn-excel"
                    onClick={exportTicgnExcel}
                    className="text-sm px-3 py-1.5 rounded-lg border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition inline-flex items-center gap-1.5"
                    title="Télécharger la liste filtrée au format Excel"
                  >
                    <FileSpreadsheet className="w-4 h-4" /> Excel
                  </button>
                </div>
              </div>
            )}
            {rapportsTICGN.length === 0 ? (
              <div className="text-center py-12 text-[#687175]">Aucun rapport TICGN pour le moment.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#BFC6CC]">
                    <th className="text-left px-4 py-3 text-[#687175] font-medium">Entreprise</th>
                    <th className="text-left px-4 py-3 text-[#687175] font-medium">SIREN</th>
                    <th className="text-right px-4 py-3 text-[#687175] font-medium">Conso (MWh)</th>
                    <th className="text-center px-4 py-3 text-[#687175] font-medium">Éligibilité</th>
                    <th className="text-right px-4 py-3 text-[#687175] font-medium">Économies</th>
                    <th className="text-right px-4 py-3 text-[#687175] font-medium">Date</th>
                    <th className="text-right px-4 py-3 text-[#687175] font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rapportsTICGNFiltres.map((r, idx) => (
                    <tr key={r.id} className="border-t border-[#BFC6CC] hover:bg-[#FFF7ED]">
                      <td className="px-4 py-4 font-medium text-[#191D1E]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{r.nom_entreprise}</span>
                          {(() => {
                            const sirenCO2 = !!r.soumis_quotas_co2;
                            const sitesCO2 = (r.pces_data || []).some(p => {
                              if (sirenCO2) return true;
                              // En multi, on retrouve la coche site dans pces_data via siret
                              return false;
                            });
                            const sitesCO2Direct = (r.sites || []).filter(s => s.soumis_quotas_co2).length;
                            // Si SIREN est coché → on affiche un badge global, sinon on liste les sites concernés
                            if (sirenCO2) {
                              return <span className="px-1.5 py-0.5 bg-orange-100 text-orange-800 rounded text-[10px] font-semibold" title="Soumis aux quotas CO2 (SEQE-UE) au niveau SIREN" data-testid={`co2-badge-siren-${r.id}`}>CO2</span>;
                            }
                            if (sitesCO2Direct > 0) {
                              return <span className="px-1.5 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 rounded text-[10px] font-semibold" title={`${sitesCO2Direct} site(s) déclarés Quotas CO2`} data-testid={`co2-badge-sites-${r.id}`}>CO2 × {sitesCO2Direct}</span>;
                            }
                            // Compatibilité ancienne structure (eventuellement)
                            return null;
                          })()}
                          {r.valeur_production_eligible && (
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded text-[10px] font-semibold" title="Valeur de Production éligible (>3%)" data-testid={`vp-badge-${r.id}`}>VP</span>
                          )}
                          {echeanceIds.ticgn.has(r.id) && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300"
                              title="Échéance CERFA proche — voir bandeau"
                              data-testid={`echeance-badge-ticgn-${r.id}`}
                            >
                              <AlertTriangle className="w-3 h-3" /> Échéance proche
                            </span>
                          )}
                          {r.client_confirmed && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300"
                              title={`Client confirmé le ${new Date(r.client_confirmed.at).toLocaleDateString('fr-FR')} par ${r.client_confirmed.by?.email || '—'}`}
                              data-testid={`client-confirmed-badge-ticgn-${r.id}`}
                            >
                              <CheckCircle2 className="w-3 h-3" /> Client OK
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 font-mono text-[#687175]">{r.siren}</td>
                      <td className="px-4 py-4 text-right">{(r.total_conso_mwh || 0).toFixed(1)}</td>
                      <td className="px-4 py-4 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          r.eligibilite_globale === 'ELIGIBLE' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}>{r.eligibilite_globale}</span>
                      </td>
                      <td className="px-4 py-4 text-right font-medium text-[#10B981]">
                        {formatCurrency((r.economies_attestation_total || 0) + Object.values(r.economies_remboursement || {}).reduce((s,v) => s+v, 0))}
                      </td>
                      <td className="px-4 py-4 text-right text-[#687175]">{formatDate(r.created_at)}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-1">
                          {r.locked && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs mr-1 flex items-center gap-1">
                              <Lock className="w-3 h-3" /> Figé
                            </span>
                          )}
                          <Link to={`/rapports/ticgn/${r.id}`} target="_blank" rel="noopener noreferrer" className="text-[#FF6B35] hover:text-[#EA580C] text-sm" data-testid={`view-ticgn-${r.id}`}>
                            Consulter
                          </Link>
                          <button
                            onClick={() => setShareTarget({ type: 'ticgn', id: r.id, label: r.nom_entreprise })}
                            className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                            title="Partager en lecture seule"
                            data-testid={`share-ticgn-${r.id}`}
                          >
                            <Share2 className="w-4 h-4" />
                          </button>
                          {canEditTicgn() && !r.locked && (
                            <Link
                              to={`/rapports/ticgn/create?edit=${r.id}`}
                              className="p-1.5 text-[#F9C80E] hover:bg-yellow-50 rounded transition-colors"
                              title="Modifier ce rapport"
                              data-testid={`edit-ticgn-${r.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Link>
                          )}
                          {canEditTicgn() && !r.locked && (
                            <button
                              onClick={() => lockRapport('ticgn', r.id, r.nom_entreprise)}
                              className="p-1.5 text-gray-400 hover:bg-gray-50 rounded transition-colors"
                              title="Figer ce rapport"
                              data-testid={`lock-ticgn-${r.id}`}
                            >
                              <Lock className="w-4 h-4" />
                            </button>
                          )}
                          {canEditTicgn() && r.locked && (
                            <button
                              onClick={() => unlockRapport('ticgn', r.id)}
                              className="p-1.5 text-orange-400 hover:bg-orange-50 rounded transition-colors"
                              title="Défiger ce rapport"
                              data-testid={`unlock-ticgn-${r.id}`}
                            >
                              <Unlock className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => downloadPdf({
                              url: `/api/ticgn/rapports/${r.id}/pdf`,
                              filename: `rapport_ticgn_${r.siren || r.nom_entreprise || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`,
                            })}
                            className="p-1.5 text-[#F97316] hover:bg-orange-50 rounded transition-colors" title="Télécharger PDF"
                            data-testid={`pdf-ticgn-${r.id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                          {canDeleteTicgn() && !r.locked && (
                            <button
                              onClick={async () => {
                                if (window.confirm(`Supprimer le rapport TICGN "${r.nom_entreprise}" ?`)) {
                                  const token = localStorage.getItem('token');
                                  await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ticgn/rapports/${r.id}`, {
                                    method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
                                  });
                                  fetchData();
                                }
                              }}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                              title="Supprimer ce rapport"
                              data-testid={`delete-ticgn-${r.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      {activeTab === 'brouillons' && (
        <div data-testid="dashboard-brouillons-tab">
          <div className="bg-white border border-[#BFC6CC] rounded-lg">
            <div className="px-6 py-4 border-b border-[#BFC6CC] flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-[#191D1E]">Brouillons en cours</h2>
                <p className="text-sm text-[#687175]">
                  {loadingBrouillons ? 'Chargement…' : `${brouillonsServer.length} brouillon(s)`}
                  {(userPermissions.role === 'admin' || userPermissions.role === 'energy_manager') && (
                    <span className="ml-2 text-xs text-[#687175]">(tous utilisateurs)</span>
                  )}
                </p>
              </div>
              <button onClick={loadBrouillons} className="text-xs text-[#397DFF] hover:underline" data-testid="reload-brouillons-btn">
                Rafraîchir
              </button>
            </div>
            {brouillonsServer.length === 0 ? (
              <div className="p-12 text-center text-[#687175]">
                <Save className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Aucun brouillon en cours.</p>
                <p className="text-sm mt-1">Utilisez le bouton "Enregistrer Brouillon" dans un wizard pour reprendre plus tard.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F5F6F9] text-[#687175] text-left">
                  <tr>
                    <th className="px-4 py-2.5 font-medium">Type</th>
                    <th className="px-4 py-2.5 font-medium">Entreprise</th>
                    <th className="px-4 py-2.5 font-medium">SIREN</th>
                    <th className="px-4 py-2.5 font-medium">Étape</th>
                    <th className="px-4 py-2.5 font-medium">Dernière modification</th>
                    {(userPermissions.role === 'admin' || userPermissions.role === 'energy_manager') && (
                      <th className="px-4 py-2.5 font-medium">Auteur</th>
                    )}
                    <th className="px-4 py-2.5 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {brouillonsServer.map(b => {
                    const typeColors = {
                      ticfe: 'bg-[#FFF9E6] text-[#B45309] border-[#F9C80E]',
                      ticgn: 'bg-[#FFF7ED] text-[#C2410C] border-[#F97316]',
                      turpe: 'bg-[#E8F4FD] text-[#1E40AF] border-[#397DFF]',
                    };
                    const navTarget = {
                      ticfe: `/rapports/create?brouillon=${b.id}`,
                      ticgn: `/rapports/ticgn/create?brouillon=${b.id}`,
                      turpe: `/analyses/turpe/create?brouillon=${b.id}`,
                    }[b.type] || '#';
                    return (
                      <tr key={b.id} className="border-t border-[#F0F0F0] hover:bg-[#FAFAFA]">
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${typeColors[b.type] || 'bg-gray-100'}`}>
                            {b.type?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium text-[#191D1E]">
                          {b.nom_entreprise || <span className="text-[#687175] italic">Sans nom</span>}
                        </td>
                        <td className="px-4 py-3 text-[#687175] font-mono text-xs">{b.siren || '—'}</td>
                        <td className="px-4 py-3 text-[#687175]">Étape {b.step || 1}</td>
                        <td className="px-4 py-3 text-[#687175]">
                          {b.updated_at ? new Date(b.updated_at).toLocaleString('fr-FR') : '—'}
                        </td>
                        {(userPermissions.role === 'admin' || userPermissions.role === 'energy_manager') && (
                          <td className="px-4 py-3 text-[#687175]">{b.user_name || b.user_email || '—'}</td>
                        )}
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => navigate(navTarget)}
                            className="text-[#397DFF] hover:underline mr-3"
                            data-testid={`resume-brouillon-${b.id}`}
                          >
                            Reprendre
                          </button>
                          <button
                            onClick={() => supprimerBrouillon(b.id)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`delete-brouillon-${b.id}`}
                            title="Supprimer ce brouillon"
                          >
                            <Trash2 className="w-4 h-4 inline" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      {shareTarget && (
        <ShareModal
          open={!!shareTarget}
          onClose={() => setShareTarget(null)}
          rapportType={shareTarget.type}
          rapportId={shareTarget.id}
          rapportLabel={shareTarget.label}
        />
      )}
    </div>
  );
};

export default DashboardUnified;
